/**
 * Engine queries (W1-05b engine-graph; ARCHITECTURE.md §7.1, §7.4, §7.7, DESIGN.md §6.2, rpe-format.md F§7).
 *
 * Read-only questions the editor asks of the compiled graph (callers sync the graph first):
 *  - `channelValue` / `sampleChannel`: DISPLAY values in RPE units (column headers, readouts, timeline curves,
 *    Σ overlays): a layer's value or the sum over layers. They come from factor-1 chains over the lowered IR
 *    (W1-05a `LineEval.displayValue`), so they are exactly what the export carries; rendering never uses them
 *    (line alpha and the strict `alpha < 0` rule come only from `LineStateRt.alpha`). A channel the DOCUMENT does
 *    not have reads NaN ("not set"), even where the RPE-compatible profile writes neutral padding for it (padding
 *    renders exactly like the absent list); a GIF line playing automatically has a progress channel.
 *  - `floorPosition` / `sampleFloor`: the line's floor position H(t) from the active integrator (legacy or 170,
 *    package speed mode), f32 values accumulated in f64, frozen after max_time.
 *  - `sampleEffectiveSpeed`: dH/dt of that floor curve in RPE speed units (what actually moves the notes, e.g. a
 *    step for an rpe170 easingType 0 segment), which the Scroll speed column draws.
 *  - `segmentAt`: the key whose segment is active at t on a track (the last key at or before t; null before the
 *    first key), from the key beats in Phira's seconds (raw imported triples while still equal in value).
 *  - `pickNote`: the topmost note quad of a frame draw list containing a world point, in reverse draw order
 *    (preview click-to-select, lane link).
 *  - `engineStats`: counts for the status bar and diagnostics.
 * Every sampler writes `out.length` evenly spaced samples over [t0, t1] (t0 alone for a single sample).
 */

import { sampleInto } from './anim';
import { sampleEffectiveSpeed as sampleSpeedOf, sampleFloor as sampleFloorOf } from './floor/curve';
import type { EngineGraph } from './graph';
import type { LineEval } from './lineState';
import type { EngineStats, FrameDrawList, NoteQuadBatch } from './types';
import { tripleOut } from '../ir/triples';
import type { KeyId, LineId, LineProp, NoteId, TrackId } from '../model/types';
import type { Sec } from '../time/types';

/** Scratch stale-triple counter (segmentAt only reads triples). */
const NO_STALE = { staleRawTriples: 0 };

/** True when the document has the channel: a track of (line, prop) on `layer`, or on any layer for 'sum'. */
export function hasChannel(g: EngineGraph, line: LineId, prop: LineProp, layer: number | 'sum'): boolean {
  if (prop === 'gif' && (layer === 'sum' || layer === 0) && g.src.doc.lines.get(line)?.gifAutoLoop === true) {
    return true;
  }
  const idx = g.src.idx;
  if (layer === 'sum') return (idx.tracksOfLine(line).get(prop)?.length ?? 0) > 0;
  return idx.track(line, prop, layer) !== undefined;
}

/** The compiled line when the channel exists, else null. */
function displayEval(g: EngineGraph, line: LineId, prop: LineProp, layer: number | 'sum'): LineEval | null {
  const le = g.lineEvalOrNull(line);
  return le !== null && hasChannel(g, line, prop, layer) ? le : null;
}

/** Channel readout in RPE units (NaN when the line or the channel is absent). */
export function channelValue(
  g: EngineGraph,
  line: LineId,
  prop: LineProp,
  t: Sec,
  layer: number | 'sum' = 'sum',
): number {
  const le = displayEval(g, line, prop, layer);
  return le === null ? Number.NaN : le.displayValue(prop, t, layer);
}

/** `out.length` channel readouts evenly spaced over [t0, t1] (NaN everywhere when the channel is absent). */
export function sampleChannel(
  g: EngineGraph,
  line: LineId,
  prop: LineProp,
  layer: number | 'sum',
  t0: Sec,
  t1: Sec,
  out: Float32Array,
): void {
  const le = displayEval(g, line, prop, layer);
  if (le === null) {
    out.fill(Number.NaN);
    return;
  }
  sampleInto((t) => le.displayValue(prop, t, layer), t0, t1, out);
}

/** Floor position H(t) of a line (Phira height units; NaN for an unknown line). */
export function floorPosition(g: EngineGraph, line: LineId, t: Sec): number {
  const le = g.lineEvalOrNull(line);
  return le === null ? Number.NaN : le.floor.heightAt(t);
}

/** `out.length` floor positions evenly spaced over [t0, t1]. */
export function sampleFloor(g: EngineGraph, line: LineId, t0: Sec, t1: Sec, out: Float32Array): void {
  const le = g.lineEvalOrNull(line);
  if (le === null) {
    out.fill(Number.NaN);
    return;
  }
  sampleFloorOf(le.floor, t0, t1, out);
}

/** `out.length` effective scroll speeds (dH/dt in RPE speed units) evenly spaced over [t0, t1]. */
export function sampleEffectiveSpeed(g: EngineGraph, line: LineId, t0: Sec, t1: Sec, out: Float32Array): void {
  const le = g.lineEvalOrNull(line);
  if (le === null) {
    out.fill(Number.NaN);
    return;
  }
  sampleSpeedOf(le.floor, t0, t1, out);
}

/**
 * The key whose segment is active at t on a track: the last key whose time (Phira seconds of its beat) is <= t;
 * null before the first key, for NaN, and for an unknown or empty track.
 */
export function segmentAt(g: EngineGraph, track: TrackId, t: Sec): KeyId | null {
  const doc = g.src.doc;
  if (!doc.tracks.has(track)) return null;
  const keys = g.src.idx.keysByTrack(track);
  const tempo = g.tempo;
  const secOf = (i: number): number => {
    const k = doc.keys.get(keys[i] as KeyId);
    if (k === undefined) return Number.NaN;
    return tempo.secAtTriple(tripleOut(k.t, k.src?.rawStart, NO_STALE));
  };
  let lo = 0;
  let hi = keys.length;
  // First index whose time is > t.
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if (secOf(mid) <= t) lo = mid + 1;
    else hi = mid;
  }
  return lo === 0 ? null : (keys[lo - 1] ?? null);
}

// ---------------------------------------------------------------------------------------------------------------
// Note picking
// ---------------------------------------------------------------------------------------------------------------

/** Cross product sign of (b - a) x (p - a). */
function side(ax: number, ay: number, bx: number, by: number, px: number, py: number): number {
  return (bx - ax) * (py - ay) - (by - ay) * (px - ax);
}

/**
 * True when (x, y) lies inside (or on the edge of) quad k of `batch`. Quads are convex parallelograms or
 * trapezoids in either winding; degenerate (zero-area) quads never contain a point.
 */
export function quadContains(batch: NoteQuadBatch, k: number, x: number, y: number): boolean {
  const p = batch.pos;
  const o = k * 8;
  const x0 = p[o] ?? 0;
  const y0 = p[o + 1] ?? 0;
  const x1 = p[o + 2] ?? 0;
  const y1 = p[o + 3] ?? 0;
  const x2 = p[o + 4] ?? 0;
  const y2 = p[o + 5] ?? 0;
  const x3 = p[o + 6] ?? 0;
  const y3 = p[o + 7] ?? 0;
  const area = side(x0, y0, x1, y1, x2, y2) + side(x0, y0, x2, y2, x3, y3);
  if (!(Math.abs(area) > 0)) return false;
  const s0 = side(x0, y0, x1, y1, x, y);
  const s1 = side(x1, y1, x2, y2, x, y);
  const s2 = side(x2, y2, x3, y3, x, y);
  const s3 = side(x3, y3, x0, y0, x, y);
  if (area > 0) return s0 >= 0 && s1 >= 0 && s2 >= 0 && s3 >= 0;
  return s0 <= 0 && s1 <= 0 && s2 <= 0 && s3 <= 0;
}

/**
 * The note of the topmost quad of `list` containing the world point (x, y): buckets are visited in reverse draw
 * order and the quads of a bucket from the last pushed. Quads without a source note are skipped. Null when no
 * quad contains the point.
 */
export function pickNote(list: FrameDrawList, x: number, y: number): NoteId | null {
  const batch = list.notes;
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  const buckets = batch.buckets;
  if (buckets.length === 0) {
    for (let k = Math.min(batch.count, batch.note.length) - 1; k >= 0; k--) {
      const id = batch.note[k] ?? -1;
      if (id >= 0 && quadContains(batch, k, x, y)) return id as NoteId;
    }
    return null;
  }
  for (let b = buckets.length - 1; b >= 0; b--) {
    const bucket = buckets[b];
    if (bucket === undefined) continue;
    const end = Math.min(bucket.start + bucket.count, batch.count);
    for (let k = end - 1; k >= bucket.start; k--) {
      const id = batch.note[k] ?? -1;
      if (id >= 0 && quadContains(batch, k, x, y)) return id as NoteId;
    }
  }
  return null;
}

// ---------------------------------------------------------------------------------------------------------------
// Stats
// ---------------------------------------------------------------------------------------------------------------

/** Engine counters: document sizes, lowered sizes and the last sync / frame durations. */
export function engineStats(g: EngineGraph, lastFrameMs: number): EngineStats {
  const doc = g.src.doc;
  let realNotes = 0;
  let irEvents = 0;
  for (const id of g.order) {
    const node = g.node(id);
    if (node === undefined || node.ir === null) continue;
    realNotes += node.ir.numOfNotes;
    irEvents += node.irEvents;
  }
  return {
    lines: g.order.length,
    notes: doc.notes.size,
    realNotes,
    keys: doc.keys.size,
    irEvents,
    lastSyncMs: g.lastSyncMs,
    lastFrameMs,
  };
}
